/**
 * PublicacaoTemplate — CRUD de Publicacao (tenant) + ação "Liberar" (Publicacao.liberar).
 *
 * Adaptado de hay-hotspot/assets/reactweb/src/pages/Auth/Publicacao/PublicacaoTemplate.js
 * (Fase 3.2) — removido `quantidaderouterboards` (específico de hotspot), mantido
 * `quantidadeusuarios`/`limitedados` (genéricos).
 */
import React, { Component } from 'react';
import { confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { TabPanel } from 'primereact/tabview';

import { TabViewComErro } from 'hay-core-react';
import { EntityGrid } from 'hay-core-react';
import { ModelTextInput } from 'hay-core-react';
import { ModelDropDown } from 'hay-core-react';
import { UtilService } from 'hay-core-react';
import { ApiService } from 'hay-core-react';
import { Publicacao } from 'hay-core-react';
import { Usuario } from 'hay-core-react';
import PublicacaoModeloiaEnum from '../../../enums/PublicacaoModeloiaEnum';

const model = {
  descricao: { exibir: 'main.publicacao.descricao', visivel: true, validacao: { obrigatorio: true } },
  cnameapp: { exibir: 'main.publicacao.cnameapp', visivel: true, validacao: { obrigatorio: true } },
  proprietario: {
    exibir: 'main.publicacao.proprietario',
    visivel: true,
    tipo: 'relacao',
    camporelacao: 'nome',
    entidade: 'proprietario.entidade',
    template: 'proprietario.template',
    validacao: { obrigatorio: true },
  },
  quantidadeusuarios: { exibir: 'main.publicacao.quantidadeusuarios', visivel: true, tipo: 'numeric', keyfilter: 'num' },
  limitedados: { exibir: 'main.publicacao.limitedados', visivel: true, tipo: 'numeric', keyfilter: 'num' },
  modeloia: { exibir: 'main.publicacao.modeloia', visivel: true, tipo: 'enum', entidade: 'publicacao.modeloia' },
  avaliacaoiamaxtokens: { exibir: 'main.publicacao.avaliacaoiamaxtokens', visivel: true, tipo: 'numeric', keyfilter: 'num' },
};

const renderers = {
  proprietario: { entidade: Usuario, template: (rowData) => rowData.proprietario?.nome || rowData.proprietario?.id || '' },
  'publicacao.modeloia': PublicacaoModeloiaEnum,
};

// Consulta ao vivo do custo máximo (pior caso, assume 100% do teto de tokens usado como saída —
// ver IaPrecoService.calcularCustoMaximo no backend) enquanto o admin ajusta modelo/teto no
// formulário, antes mesmo de salvar.
class CustoEstimadoIa extends Component {
  state = { custoMaximo: null };

  componentDidMount() {
    this.consultar();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.modeloia !== this.props.modeloia || prevProps.maxTokens !== this.props.maxTokens) {
      this.consultar();
    }
  }

  async consultar() {
    const { modeloia, maxTokens } = this.props;
    if (!maxTokens) return this.setState({ custoMaximo: null });
    const resposta = await ApiService.post('publicacao/custoestimadoia', {
      modeloia,
      avaliacaoiamaxtokens: maxTokens,
    }, false);
    this.setState({ custoMaximo: resposta?.data?.custoMaximo ?? null });
  }

  render() {
    const { custoMaximo } = this.state;
    if (custoMaximo === null) return null;
    return (
      <Message
        severity="info"
        className="w-full"
        text={`${UtilService.traduzir('main.publicacao.custoestimadoia')}: US$ ${custoMaximo.toFixed(4)}`}
      />
    );
  }
}

class PublicacaoFormulario extends Component {
  render() {
    const { model, contexto } = this.props;
    return (
      <TabViewComErro tentativaSalvar={contexto.state.tentativaSalvar} className="col-12 mt-2">
        <TabPanel header={UtilService.traduzir('main.publicacao.aba.informacoesbasicas')} leftIcon="pi pi-info-circle mr-2">
          <div className="grid w-full">
            <div className="field col-12 md:col-6 mt-3"><ModelTextInput model={model.descricao} contexto={contexto} /></div>
            <div className="field col-12 md:col-6 mt-3"><ModelTextInput model={model.cnameapp} contexto={contexto} /></div>
            <div className="field col-12 md:col-6 mt-3"><ModelDropDown model={model.proprietario} contexto={contexto} /></div>
            <div className="field col-12 md:col-6 mt-3"><ModelTextInput model={model.quantidadeusuarios} contexto={contexto} /></div>
            <div className="field col-12 md:col-6 mt-3"><ModelTextInput model={model.limitedados} contexto={contexto} /></div>
            <div className="field col-12 md:col-6 mt-3"><ModelDropDown model={model.modeloia} contexto={contexto} /></div>
            <div className="field col-12 md:col-6 mt-3"><ModelTextInput model={model.avaliacaoiamaxtokens} contexto={contexto} /></div>
            <div className="field col-12">
              <CustoEstimadoIa modeloia={model.modeloia?.value?.id} maxTokens={model.avaliacaoiamaxtokens?.value} />
            </div>
          </div>
        </TabPanel>
      </TabViewComErro>
    );
  }
}

class PublicacaoTemplate extends Component {

  processos() {
    const { permissoes } = this.props;
    const t = (chave) => UtilService.traduzir(chave);

    const podeLiberar = permissoes?.publicacao?.processos?.some(
      e => e.state === 'main.publicacao.liberar' && e.permitido
    );

    const lista = [];

    if (podeLiberar) {
      lista.push({
        label: 'main.publicacao.liberar',
        icon: 'pi pi-send',
        disabled: true,
        ativo: (processo, selected) => { processo.disabled = selected.length !== 1; return processo; },
        command: () => {
          const row = this.gridRef?.getSelected()?.[0];
          if (!row) return;
          confirmDialog({
            header: t('main.publicacao.liberar.confirmacao.header'),
            message: t('main.publicacao.liberar.confirmacao.mensagem'),
            icon: 'pi pi-exclamation-triangle',
            className: 'w-4',
            draggable: false,
            defaultFocus: 'reject',
            acceptClassName: 'p-button-warning',
            accept: async () => {
              try {
                await Publicacao.liberar(row.id);
                UtilService.showToast({ severity: 'success', summary: t('main.publicacao.liberar.sucesso') });
              } catch {
                UtilService.showToast({ severity: 'error', summary: t('main.publicacao.liberar.erro') });
              }
            },
          });
        },
      });
    }

    return lista;
  }

  render() {
    return (
      <EntityGrid
        ref={(ref) => this.gridRef = ref}
        entidade={Publicacao}
        model={model}
        identidade="main.publicacao"
        permissoes={this.props.permissoes}
        icone="pi-globe"
        storageKey="dt-publicacoes"
        processos={this.processos()}
        renderers={renderers}
        configuracao={this.props.configuracao}
        navigate={this.props.navigate}
        location={this.props.location}
        searchparams={this.props.searchparams}
        formulario={(m, ctx) => <PublicacaoFormulario model={m} contexto={ctx} />}
      />
    );
  }
}

export default PublicacaoTemplate;
